import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DepartmentCreateInputObject,
  DepartmentUpdateInputObject,
  FilterGroupingEnum,
  LinkingInputObject,
  TaskScopeNameEnum,
  useTasksSearchListLazyQuery,
} from 'graphql-common';
import _get from 'lodash/get';
import { Values } from '@lib/interfaces/form';
import toast from '@lib/components/Toast/Toast';
import { APP_URLS } from 'constants/urls';
import getDepartmentPreparedDataFromValues from './getDepartmentPreparedDataFromValues';
import getDepartmentDefaultValuesFromQueryData from './getDepartmentDefaultValuesFromQueryData';
import useMutations from './useMutations';
import { UseDepartmentQueriesResult } from './useDepartmentQueries';
import { DepartmentViewMode } from '../enums';

type Args = {
  viewMode: DepartmentViewMode;
  useDepartmentQueriesResult: UseDepartmentQueriesResult;
};

export default function useDepartmentActions({
  viewMode,
  useDepartmentQueriesResult,
}: Args) {
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const useMutationsResult = useMutations();
  const isCreatePage = viewMode === DepartmentViewMode.Add;
  const [fetchTasksByGroupKeys] = useTasksSearchListLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [resolvingTaskLinks, setResolvingTaskLinks] = useState(false);

  const {
    useDepartmentQueryHookResult,
    useUsersQueryHookResult,
    accessGroups,
    linkedTasks,
    linkedInterventions,
  } = useDepartmentQueriesResult;
  const { allIds: membersIds = [], refetchAllIds } = useUsersQueryHookResult;

  const defaultValues = isCreatePage
    ? getDepartmentDefaultValuesFromQueryData(undefined, [], [], [], [])
    : getDepartmentDefaultValuesFromQueryData(
        useDepartmentQueryHookResult.data,
        membersIds,
        accessGroups,
        linkedTasks,
        linkedInterventions,
      );

  const onDepartmentCreate = (values: DepartmentCreateInputObject) => {
    useMutationsResult.departmentCreateMutation({
      variables: {
        attributes: values,
      },
      onCompleted: (res) => {
        const newId = res?.data?.id;
        toast({ content: t('create-department-success') });
        if (newId) {
          navigate(
            APP_URLS.dashboard.departments.view.getDynamicPath({
              pathParts: { id: newId },
            }),
          );
        }
      },
    });
  };

  const onDepartmentUpdate = (values: DepartmentUpdateInputObject) => {
    useMutationsResult.departmentUpdateMutation({
      variables: {
        id,
        attributes: values,
      },
      onCompleted: () => {
        toast({ content: t('update-department-success') });
        if (refetchAllIds) refetchAllIds();
        navigate(
          APP_URLS.dashboard.departments.view.getDynamicPath({
            pathParts: { id },
          }),
        );
      },
    });
  };

  // The tasks field (via TasksTableMultiselect) stores values keyed by
  // the recurring group's `groupKey`, but `departmentCreate`/`departmentUpdate`
  // validates `taskLinks[].id` as a real, currently-active `Task.id` and
  // resolves it to `groupKey` internally — so groupKeys must be translated
  // to a live task id before submit or the BE rejects them as invalid.
  const resolveTaskLinks = async (
    taskLinks: LinkingInputObject[] | null | undefined,
  ) => {
    if (!taskLinks?.length) return taskLinks ?? null;
    const groupKeys = taskLinks.map(({ id: groupKey }) => groupKey);
    setResolvingTaskLinks(true);
    const { data } = await fetchTasksByGroupKeys({
      variables: {
        limit: groupKeys.length,
        scope: { name: TaskScopeNameEnum.Active },
        filters: [
          {
            groupKey: {
              grouping: FilterGroupingEnum.And,
              predicate: { in: groupKeys },
            },
          },
        ],
      },
    });
    setResolvingTaskLinks(false);
    const taskIdByGroupKey = new Map(
      (
        _get(data, 'data.collection', []) as { id: string; groupKey: string }[]
      ).map(({ id: taskId, groupKey }) => [groupKey, taskId]),
    );
    return taskLinks.map(({ id: groupKey, unlink }) => ({
      id: taskIdByGroupKey.get(groupKey) ?? groupKey,
      unlink,
    }));
  };

  const onSubmit = async (values: Values) => {
    const preparedData = getDepartmentPreparedDataFromValues(
      values,
      defaultValues,
    );
    const taskLinks = await resolveTaskLinks(preparedData.taskLinks);
    const resolvedData = { ...preparedData, taskLinks };
    if (viewMode === DepartmentViewMode.Add) {
      onDepartmentCreate(resolvedData);
    } else if (viewMode === DepartmentViewMode.Edit) {
      onDepartmentUpdate(resolvedData);
    }
  };

  const loading =
    useDepartmentQueryHookResult.loading ||
    resolvingTaskLinks ||
    useMutationsResult.departmentCreateMutationResult.loading ||
    useMutationsResult.departmentUpdateMutationResult.loading;

  const mutationError =
    useMutationsResult.departmentCreateMutationResult.error ||
    useMutationsResult.departmentUpdateMutationResult.error;

  return {
    defaultValues,
    id,
    isCreatePage,
    loading,
    mutationError,
    onSubmit,
  };
}
